/**
 * Shared export utilities — PDF (jsPDF) and Excel/CSV.
 * Used by Employees, Users, Leave, and any other module that needs data export.
 *
 * Both exportToPdf and exportToExcel now carry the standard company letterhead (logo, name,
 * address/contact, generated-by stamp) — see src/lib/reportBranding.ts for the source of that
 * data and src/lib/xlsxExport.ts for the Excel implementation. Update those two files, not
 * this one, to change what the letterhead looks like.
 */
import { exportToXlsx, exportSectionsToXlsx } from "./xlsxExport";
import { getLogoDataUrl, getReportLetterhead, getGeneratedStamp, LOGO_ASPECT } from "./reportBranding";

// ─── helpers ──────────────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function escapeCsv(v: unknown): string {
  const s = String(v ?? "");
  return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
}

// ─── CSV export ───────────────────────────────────────────────────────────────
// Plain CSV has no styling/embedding capability, so no letterhead here — it's a raw data
// interchange format, not a "report" in the visual sense. Left as-is intentionally.

export function exportToCsv(
  headers: string[],
  rows: (string | number | null | undefined)[][],
  filename: string,
) {
  const lines = [headers.map(escapeCsv).join(","), ...rows.map((r) => r.map(escapeCsv).join(","))];
  downloadBlob(new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" }), `${filename}.csv`);
}

// ─── Excel export ─────────────────────────────────────────────────────────────
// Delegates to the shared, letterheaded xlsx exporter — kept as a thin wrapper so existing
// (or future) callers using this exact signature don't need to change.

export function exportToExcel(
  title: string,
  headers: string[],
  rows: (string | number | null | undefined)[][],
  filename: string,
  generatedBy?: string | null,
): Promise<void> {
  return exportToXlsx({ title, headers, rows, filename, generatedBy });
}

// ─── PDF export (jsPDF) ───────────────────────────────────────────────────────

type PdfOptions = {
  title: string;
  subtitle?: string;
  headers: string[];
  rows: (string | number | null | undefined)[][];
  filename: string;
  colWidths?: number[];
  /** Display name/email of whoever ran the export — shown in the generated-by stamp. */
  generatedBy?: string | null;
};

/**
 * jsPDF's `{ maxWidth }` text option silently *wraps* onto extra lines rather than truncating —
 * fine for a single block of prose, but fatal for a fixed-row-height table: a long header like
 * "Annual Leave — Entitled" wraps to 2-3 lines inside a 9mm header band and spills down into the
 * row below, reading as "overlapping data" (this is exactly what happened on the Leave Balance
 * Matrix export, which can have 20+ narrow columns). Truncate with an ellipsis instead so every
 * cell always renders on exactly one line, matching its reserved row height.
 */
function fitText(doc: InstanceType<typeof import("jspdf").jsPDF>, text: string, maxW: number): string {
  if (maxW <= 0) return "";
  if (doc.getTextWidth(text) <= maxW) return text;
  let t = text;
  while (t.length > 1 && doc.getTextWidth(`${t}…`) > maxW) {
    t = t.slice(0, -1);
  }
  return t.length > 0 ? `${t}…` : "";
}

/**
 * Default column widths when the caller doesn't pass explicit `colWidths` ratios. Equal-width
 * division looks fine for a handful of similarly-sized columns but falls apart on wide tables
 * (e.g. a name/department column sitting next to a dozen 3-digit numeric columns) — those short
 * columns waste space while the text columns get squeezed and truncated hard. Weight each column
 * by its longest content (header or cell) instead, with a floor so no column collapses to
 * unreadable width.
 */
function autoColRatios(headers: string[], rows: (string | number | null | undefined)[][]): number[] {
  const MIN_CHARS = 6;
  const weights = headers.map((h, i) => {
    let max = h.length;
    for (const row of rows) {
      const len = String(row[i] ?? "").length;
      if (len > max) max = len;
    }
    return Math.max(max, MIN_CHARS);
  });
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  return weights.map((w) => w / total);
}

export async function exportToPdf({ title, subtitle, headers, rows, filename, colWidths, generatedBy }: PdfOptions) {
  const { jsPDF } = await import("jspdf");
  const landscape = headers.length > 5;
  const doc = new jsPDF({ orientation: landscape ? "landscape" : "portrait" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const usableW = pageW - margin * 2;
  const ratios = colWidths?.length === headers.length ? colWidths : autoColRatios(headers, rows);
  const colW = ratios.map((r) => r * usableW);
  // Many-column tables (wide matrices like Leave Balances) get a smaller font so more characters
  // fit per column before truncation has to kick in at all.
  const manyCols = headers.length > 12;
  const headerFontSize = manyCols ? 6 : 7.5;
  const dataFontSize = manyCols ? 6 : 7.5;
  const headerH = 9;
  const rowH = 7;
  let y = margin;

  // ── Letterhead: logo + company identity, drawn once above the report title bar ──────────
  const letterhead = getReportLetterhead();
  const logoH = 12;
  const logoW = logoH * LOGO_ASPECT;
  try {
    const dataUrl = await getLogoDataUrl();
    doc.addImage(dataUrl, "PNG", margin, y, logoW, logoH);
  } catch {
    // Logo fetch failed — proceed without it rather than blocking the export.
  }
  const textX = margin + logoW + 4;
  doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(4, 120, 87); // emerald-700
  doc.text(letterhead.companyName, textX, y + 5);
  doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 116, 139);
  doc.text(letterhead.addressLine, textX, y + 9.5);
  doc.text(letterhead.contactLine, textX, y + 13.5);
  y += logoH + 4;
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, y, margin + usableW, y);
  y += 4;

  function drawColHeaders() {
    doc.setFillColor(22, 101, 52);
    doc.rect(margin, y, usableW, headerH, "F");
    doc.setFontSize(headerFontSize); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
    let x = margin;
    headers.forEach((h, i) => {
      doc.text(fitText(doc, h.toUpperCase(), colW[i] - 4), x + 2, y + 6);
      x += colW[i];
    });
    y += headerH;
  }

  // Report title bar
  doc.setFillColor(22, 101, 52);
  doc.rect(margin, y, usableW, 10, "F");
  doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
  doc.text(title, margin + 3, y + 7);
  y += 12;
  doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 116, 139);
  doc.text([subtitle, getGeneratedStamp(generatedBy)].filter(Boolean).join("  ·  "), margin, y);
  y += 7;

  drawColHeaders();
  doc.setFontSize(dataFontSize); doc.setFont("helvetica", "normal");

  rows.forEach((row, ri) => {
    if (y + rowH > pageH - margin) { doc.addPage(); y = margin; drawColHeaders(); doc.setFontSize(dataFontSize); doc.setFont("helvetica", "normal"); }
    if (ri % 2 === 1) { doc.setFillColor(248, 250, 252); doc.rect(margin, y, usableW, rowH, "F"); }
    doc.setTextColor(30, 41, 59);
    let x = margin;
    row.forEach((cell, ci) => {
      doc.text(fitText(doc, String(cell ?? ""), colW[ci] - 4), x + 2, y + 5);
      x += colW[ci];
    });
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, y + rowH, margin + usableW, y + rowH);
    y += rowH;
  });

  const total = (doc.internal as any).getNumberOfPages?.() ?? 1;
  for (let i = 1; i <= total; i++) {
    doc.setPage(i); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
    doc.text(`Page ${i} of ${total}`, pageW - margin, pageH - 6, { align: "right" });
  }
  doc.save(`${filename}.pdf`);
}

// ─── Sectioned export (PDF + Excel) ────────────────────────────────────────────
// For reports better shown as several grouped tables (e.g. one per leave type) rather than one
// flat wide table. Each section gets its own heading band; no running/grand totals are drawn —
// callers that want a total should put it in a row of a section's own data instead.

export type ExportSection = {
  heading: string;
  headers: string[];
  rows: (string | number | null | undefined)[][];
};

type SectionedExportOptions = {
  title: string;
  subtitle?: string;
  sections: ExportSection[];
  filename: string;
  generatedBy?: string | null;
};

export function exportSectionsToExcel({
  title,
  subtitle,
  sections,
  filename,
  generatedBy,
}: SectionedExportOptions): Promise<void> {
  return exportSectionsToXlsx({ title, subtitle, sections, filename, generatedBy });
}

export async function exportSectionsToPdf({ title, subtitle, sections, filename, generatedBy }: SectionedExportOptions) {
  const { jsPDF } = await import("jspdf");
  const maxCols = sections.reduce((m, s) => Math.max(m, s.headers.length), 0);
  const landscape = maxCols > 5;
  const doc = new jsPDF({ orientation: landscape ? "landscape" : "portrait" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const usableW = pageW - margin * 2;
  const headerH = 9;
  const rowH = 7;
  const bannerH = 9;
  let y = margin;

  // ── Letterhead: logo + company identity, drawn once above the report title bar ──────────
  const letterhead = getReportLetterhead();
  const logoH = 12;
  const logoW = logoH * LOGO_ASPECT;
  try {
    const dataUrl = await getLogoDataUrl();
    doc.addImage(dataUrl, "PNG", margin, y, logoW, logoH);
  } catch {
    // Logo fetch failed — proceed without it rather than blocking the export.
  }
  const textX = margin + logoW + 4;
  doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(4, 120, 87);
  doc.text(letterhead.companyName, textX, y + 5);
  doc.setFontSize(7.5); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 116, 139);
  doc.text(letterhead.addressLine, textX, y + 9.5);
  doc.text(letterhead.contactLine, textX, y + 13.5);
  y += logoH + 4;
  doc.setDrawColor(226, 232, 240);
  doc.line(margin, y, margin + usableW, y);
  y += 4;

  // Report title bar (once for the whole document)
  doc.setFillColor(22, 101, 52);
  doc.rect(margin, y, usableW, 10, "F");
  doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
  doc.text(title, margin + 3, y + 7);
  y += 12;
  doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 116, 139);
  doc.text([subtitle, getGeneratedStamp(generatedBy)].filter(Boolean).join("  ·  "), margin, y);
  y += 9;

  function ensureSpace(h: number) {
    if (y + h > pageH - margin) { doc.addPage(); y = margin; }
  }

  sections.forEach((section) => {
    const ratios = autoColRatios(section.headers, section.rows);
    const colW = ratios.map((r) => r * usableW);
    const manyCols = section.headers.length > 12;
    const headerFontSize = manyCols ? 6 : 7.5;
    const dataFontSize = manyCols ? 6 : 7.5;

    function drawColHeaders() {
      doc.setFillColor(22, 101, 52);
      doc.rect(margin, y, usableW, headerH, "F");
      doc.setFontSize(headerFontSize); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
      let x = margin;
      section.headers.forEach((h, i) => {
        doc.text(fitText(doc, h.toUpperCase(), colW[i] - 4), x + 2, y + 6);
        x += colW[i];
      });
      y += headerH;
    }

    // Keep a section's banner glued to at least its header row, so a banner never lands alone
    // at the bottom of a page with its table stranded on the next.
    ensureSpace(bannerH + 2 + headerH + rowH);

    doc.setFillColor(27, 67, 50);
    doc.rect(margin, y, usableW, bannerH, "F");
    doc.setFontSize(10.5); doc.setFont("helvetica", "bold"); doc.setTextColor(255, 255, 255);
    doc.text(section.heading, margin + 3, y + 6.3);
    y += bannerH + 2;

    drawColHeaders();

    if (!section.rows.length) {
      doc.setFontSize(8); doc.setFont("helvetica", "italic"); doc.setTextColor(148, 163, 184);
      doc.text("No employees are currently entitled to this leave type.", margin + 2, y + 5);
      y += rowH + 6;
      return;
    }

    doc.setFontSize(dataFontSize); doc.setFont("helvetica", "normal");
    section.rows.forEach((row, ri) => {
      if (y + rowH > pageH - margin) {
        doc.addPage(); y = margin; drawColHeaders();
        doc.setFontSize(dataFontSize); doc.setFont("helvetica", "normal");
      }
      if (ri % 2 === 1) { doc.setFillColor(248, 250, 252); doc.rect(margin, y, usableW, rowH, "F"); }
      doc.setTextColor(30, 41, 59);
      let x = margin;
      row.forEach((cell, ci) => {
        doc.text(fitText(doc, String(cell ?? ""), colW[ci] - 4), x + 2, y + 5);
        x += colW[ci];
      });
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y + rowH, margin + usableW, y + rowH);
      y += rowH;
    });

    y += 6; // gap before next section's banner
  });

  const totalPages = (doc.internal as any).getNumberOfPages?.() ?? 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i); doc.setFontSize(7); doc.setTextColor(148, 163, 184);
    doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 6, { align: "right" });
  }
  doc.save(`${filename}.pdf`);
}
